import type { NewPostRequest } from '@ecowheels/types';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button, Card, Input, PageHeader } from '@/components/ui';

type PendingImage = { name: string; base64: string; alt: string };

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    // The API does `Buffer.from(base64,'base64')`, so it wants the raw payload
    // without the `data:image/...;base64,` prefix that readAsDataURL adds.
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Post editor.
 *
 * The Angular version had a bespoke WYSIWYG (html-viewer + insert-image +
 * insert-link components) that produced the same `html` string this posts.
 * This is deliberately a plain HTML editor with a live preview: the rich
 * editor is a product decision worth making on purpose rather than
 * reconstructing by inference. The API contract is unchanged.
 *
 * Images: attach a file, give it a name and alt text, then reference the name
 * in the HTML (e.g. `<img src="hero">`). The API rewrites each `name"` to the
 * stored path and injects the alt text.
 */
export function NewPostPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [cardAlt, setCardAlt] = useState('');
  const [html, setHtml] = useState('<h2>Cím</h2>\n<p>Szöveg…</p>');
  const [images, setImages] = useState<PendingImage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: NewPostRequest) => api.post('/admin/post', body),
    onSuccess: () => navigate('/posts'),
    onError: () => setError('A mentés nem sikerült. Kérjük próbálja újra.'),
  });

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    const added = await Promise.all(
      picked.map(async (file) => ({
        // The API builds `<id>_<name>.jpg`, so a name with a slash or quote
        // would produce a broken path or malformed HTML.
        name: file.name.replace(/\.[^.]+$/, '').replace(/[^\w-]/g, '-'),
        base64: await readAsBase64(file),
        alt: '',
      }))
    );
    setImages((prev) => [...prev, ...added]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const missingAlt = images.filter((img) => !img.alt.trim());
    if (missingAlt.length > 0) {
      // Alt text is required for accessibility and it is what the API injects
      // into the rewritten <img>; the Angular editor let it through empty.
      setError('Minden képhez adjon meg alt szöveget.');
      return;
    }

    create.mutate({ title, subtitle, content, cardAlt, html, images });
  }

  return (
    <>
      <PageHeader title="Új bejegyzés" />

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label htmlFor="title" className="text-sm text-white/80">
              Cím
            </label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div>
            <label htmlFor="subtitle" className="text-sm text-white/80">
              Alcím
            </label>
            <Input
              id="subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label htmlFor="content" className="text-sm text-white/80">
              Rövid összefoglaló
            </label>
            <Input
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label htmlFor="cardAlt" className="text-sm text-white/80">
              Kártya alt szöveg
            </label>
            <Input
              id="cardAlt"
              value={cardAlt}
              onChange={(e) => setCardAlt(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label htmlFor="html" className="text-sm text-white/80">
              Tartalom (HTML)
            </label>
            <textarea
              id="html"
              rows={16}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/15 bg-white/[0.04] px-3 py-2 font-mono text-sm text-white outline-none focus:border-brand"
            />
          </div>

          <Card className="p-4">
            <p className="text-sm font-medium">Képek</p>
            <p className="mt-1 text-xs text-white/50">
              Töltsön fel képet, majd hivatkozzon rá a HTML-ben a nevével:{' '}
              <code className="text-brand">&lt;img src=&quot;nev&quot;&gt;</code>
            </p>

            <label className="mt-3 flex cursor-pointer items-center justify-center rounded-md border border-dashed border-brand/60 px-4 py-3 text-sm text-brand hover:bg-brand/5">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onFiles}
                className="sr-only"
              />
              Kép hozzáadása
            </label>

            {images.length > 0 && (
              <ul className="mt-3 space-y-2">
                {images.map((img, i) => (
                  <li key={img.name + i} className="rounded-md border border-white/10 p-2">
                    <div className="flex items-center gap-2">
                      <img
                        src={`data:image/*;base64,${img.base64}`}
                        alt=""
                        className="h-10 w-10 rounded object-cover"
                      />
                      <code className="flex-1 truncate text-xs text-brand">{img.name}</code>
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                        className="text-white/50 hover:text-red-400"
                        aria-label={`${img.name} törlése`}
                      >
                        ×
                      </button>
                    </div>
                    <Input
                      value={img.alt}
                      placeholder="Alt szöveg (kötelező)"
                      onChange={(e) =>
                        setImages((prev) =>
                          prev.map((p, j) => (j === i ? { ...p, alt: e.target.value } : p))
                        )
                      }
                      className="mt-2 text-xs"
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Mentés…' : 'Mentés'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate('/posts')}>
              Mégse
            </Button>
          </div>
        </div>

        {/* Live preview — the Angular editor had none, so you published blind. */}
        <div>
          <p className="text-sm text-white/80">Előnézet</p>
          <Card className="mt-1 p-4">
            <div
              className="[&_a]:text-brand [&_h2]:mt-2 [&_h2]:text-xl [&_h2]:font-semibold [&_img]:max-w-full [&_img]:rounded [&_p]:mt-2 [&_p]:text-white/70"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </Card>
        </div>
      </form>
    </>
  );
}
